import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Loader2 } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { COMPANY } from "@/data/site";
import { createEnquiry } from "@/lib/api";

const inputCls = "bg-white border-slate-300 focus:border-brand focus-visible:ring-brand/30 rounded-lg h-12 text-slate-900";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) return toast.error("Please add your name and message.");
    setLoading(true);
    try {
      await createEnquiry(form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Could not send. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-brand font-semibold uppercase tracking-[0.2em] text-xs mb-3">Get in touch</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">We're ready when you are</h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Questions about a booking, account travel or something else? Reach out any time.
            </p>
            <div className="mt-10 space-y-4">
              <a href={`tel:${COMPANY.phoneRaw}`} data-testid="contact-phone" className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 hover:border-brand transition-colors">
                <span className="grid place-items-center h-12 w-12 rounded-lg bg-brand-50"><Phone className="h-5 w-5 text-brand" strokeWidth={1.75} /></span>
                <span><span className="block text-xs uppercase tracking-widest text-slate-400 font-semibold">Call us</span><span className="text-slate-800 font-medium">{COMPANY.phone}</span></span>
              </a>
              <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 hover:border-brand transition-colors">
                <span className="grid place-items-center h-12 w-12 rounded-lg bg-brand-50"><MessageCircle className="h-5 w-5 text-brand" strokeWidth={1.75} /></span>
                <span><span className="block text-xs uppercase tracking-widest text-slate-400 font-semibold">WhatsApp</span><span className="text-slate-800 font-medium">Message us</span></span>
              </a>
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 hover:border-brand transition-colors">
                <span className="grid place-items-center h-12 w-12 rounded-lg bg-brand-50"><Mail className="h-5 w-5 text-brand" strokeWidth={1.75} /></span>
                <span><span className="block text-xs uppercase tracking-widest text-slate-400 font-semibold">Email</span><span className="text-slate-800 font-medium">{COMPANY.email}</span></span>
              </a>
              <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4">
                <span className="grid place-items-center h-12 w-12 rounded-lg bg-brand-50"><MapPin className="h-5 w-5 text-brand" strokeWidth={1.75} /></span>
                <span><span className="block text-xs uppercase tracking-widest text-slate-400 font-semibold">Base</span><span className="text-slate-800 font-medium">{COMPANY.address}</span></span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form data-testid="contact-form" onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Send a message</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-2 block">Name *</Label>
                  <Input data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-2 block">Phone</Label>
                  <Input data-testid="contact-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-2 block">Email</Label>
                  <Input data-testid="contact-email-input" className={inputCls} value={form.email} onChange={set("email")} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-2 block">Message *</Label>
                  <Textarea data-testid="contact-message" rows={5} className="bg-white border-slate-300 focus:border-brand focus-visible:ring-brand/30 rounded-lg text-slate-900" value={form.message} onChange={set("message")} />
                </div>
              </div>
              <Button type="submit" data-testid="contact-submit" disabled={loading} className="mt-6 w-full bg-brand hover:bg-brand-dark text-white rounded-lg py-4 h-auto font-semibold transition-colors active:scale-[0.99]">
                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</> : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
